import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand/Logo";
import fleetApi from "@/lib/fleetApi";
import { visibleNotifications } from "@/lib/notifications";
import { isOpenRequest, REQUESTS_CHANGED } from "@/lib/assignRequests";
import { DAY_REVIEW_COUNT_CHANGED } from "@/pages/portal/DayReviewPage";
import { boardTrips } from "@/lib/tripsFetch";
import TrialBanner from "@/components/portal/TrialBanner";
import {
  LayoutDashboard, Navigation, Truck, ShieldAlert, BarChart3, Settings,
  LogOut, Menu, X, ChevronDown, Upload, Repeat, Bell, CreditCard, Lock, LifeBuoy, UserPlus, Inbox, Sunrise, ClipboardCheck, FileSearch, History,
} from "lucide-react";

const NAV = [
  { to: "/portal/dashboard", label: "Operations Command", icon: LayoutDashboard, roles: ["owner", "supervisor", "dispatcher", "administrator", "fleet_manager"] },
  { to: "/portal/dispatch", label: "Trips & Dispatch", icon: Navigation, roles: ["owner", "supervisor", "dispatcher", "administrator", "fleet_manager"] },
  { to: "/portal/requests", label: "Assign Requests", icon: Inbox, roles: ["owner"] },
  { to: "/portal/day-review", label: "Day Review", icon: Sunrise, roles: ["owner"] },
  { to: "/portal/history", label: "Trip History", icon: History, roles: ["owner"] },
  { to: "/portal/standing-orders", label: "Standing Orders", icon: Repeat, roles: ["owner", "supervisor", "dispatcher", "administrator", "fleet_manager"] },
  { to: "/portal/import", label: "Import Trips", icon: Upload, roles: ["owner", "supervisor", "dispatcher", "fleet_manager"], feature: "import" },
  { to: "/portal/fleet", label: "Drivers & Fleet", icon: Truck, roles: ["owner", "supervisor", "dispatcher", "administrator", "fleet_manager"] },
  { to: "/portal/inspections", label: "Inspections", icon: ClipboardCheck, roles: ["owner", "supervisor", "dispatcher", "administrator", "fleet_manager"] },
  { to: "/portal/ticket-lookup", label: "Ticket Lookup", icon: FileSearch, roles: ["owner", "supervisor", "dispatcher", "administrator", "fleet_manager"] },
  { to: "/portal/incidents", label: "Incidents & Compliance", icon: ShieldAlert, roles: ["owner", "supervisor", "administrator", "fleet_manager"], feature: "incidents" },
  { to: "/portal/reports", label: "Reports & Costs", icon: BarChart3, roles: ["owner", "supervisor", "administrator", "fleet_manager"] },
  { to: "/portal/team", label: "Team & Invites", icon: UserPlus, roles: ["owner", "administrator"] },
  { to: "/portal/settings", label: "Org & Roles", icon: Settings, roles: ["owner", "administrator"] },
  { to: "/portal/billing", label: "Plans & Billing", icon: CreditCard, roles: ["owner", "administrator"] },
];

const ROLE_LABEL = {
  owner: "Owner", supervisor: "Supervisor", dispatcher: "Dispatcher", administrator: "Administrator", fleet_manager: "Fleet Manager", driver: "Driver",
};

const helpMailto = (org, user) => {
  const company = org?.name || "My company";
  const subject = `RideStamp support request — ${company}`;
  const body = `Hi RideStamp Support,\n\nCompany: ${company}\nName: ${user?.name || ""}\nEmail: ${user?.email || ""}\nRole: ${ROLE_LABEL[user?.role] || user?.role || ""}\nPage: ${window.location.pathname}\n\nHow can we help?\n\n`;
  return `mailto:support@myridestamp.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};


export default function PortalLayout() {
  const { user, organization, entitlements, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await fleetApi.get("/notifications");
        if (!active) return;
        setUnread(visibleNotifications(data, user).filter((n) => n.status === "unread").length);
      } catch (e) { /* ignore */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { active = false; clearInterval(t); };
  }, [user]);

  const [requests, setRequests] = useState(0);
  useEffect(() => {
    if (user?.role !== "owner") return;
    let active = true;
    const load = () => boardTrips()
      .then((r) => { if (active) setRequests(r.filter(isOpenRequest).length); })
      .catch(() => {});
    load();
    const t = setInterval(load, 120000);
    window.addEventListener(REQUESTS_CHANGED, load);
    return () => { active = false; clearInterval(t); window.removeEventListener(REQUESTS_CHANGED, load); };
  }, [user]);

  const [reviewCount, setReviewCount] = useState(0);
  useEffect(() => {
    if (user?.role !== "owner") return undefined;
    const load = () => fleetApi.get("/day-review?scope=review").then((r) => setReviewCount(r.data.review_count ?? (r.data.trips || []).length)).catch(() => {});
    const sync = (e) => setReviewCount(e.detail || 0);
    window.addEventListener(DAY_REVIEW_COUNT_CHANGED, sync);
    load();
    const t = setInterval(load, 600000);
    return () => { clearInterval(t); window.removeEventListener(DAY_REVIEW_COUNT_CHANGED, sync); };
  }, [user]);

  const items = NAV.filter((n) => n.roles.includes(user?.role));

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const SidebarContent = (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center border-b border-slate-800 px-5">
        <button onClick={() => navigate("/")} data-testid="portal-logo">
          <Logo variant="light" />
        </button>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-5">
        <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-widest text-slate-600">Operations</p>
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            onClick={() => setOpen(false)}
            data-testid={`sidebar-${n.to.split("/").pop()}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
                isActive
                  ? "bg-blue-600/15 text-blue-300 shadow-[inset_2px_0_0_0_#2563eb]"
                  : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-100"
              }`
            }
          >
            <n.icon className="h-4.5 w-4.5 h-4 w-4" />
            <span className="flex-1">{n.label}</span>
            {n.to === "/portal/day-review" && reviewCount > 0 && (
              <span className="min-w-[1.25rem] rounded-full bg-amber-500 px-1.5 py-0.5 text-center font-mono-data text-[10px] font-bold leading-none text-slate-950" data-testid="nav-day-review-badge">
                {reviewCount > 99 ? "99+" : reviewCount}
              </span>
            )}
            {n.to === "/portal/requests" && requests > 0 && (
              <span className="min-w-[1.25rem] rounded-full bg-amber-500 px-1.5 py-0.5 text-center font-mono-data text-[10px] font-bold leading-none text-slate-950" data-testid="nav-requests-badge">
                {requests > 99 ? "99+" : requests}
              </span>
            )}
            {n.feature && entitlements && !(entitlements.features || []).includes(n.feature) && (
              <Lock className="h-3.5 w-3.5 text-slate-600" data-testid={`nav-lock-${n.to.split("/").pop()}`} />
            )}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-800 p-4">
        <div className="rounded-lg bg-slate-800/50 p-3">
          <div className="text-xs font-semibold text-slate-200">{organization?.name}</div>
          <div className="mt-0.5 font-mono-data text-[10px] uppercase tracking-wide text-slate-500">Live · Connected</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="rs-portal flex min-h-screen bg-[#0b0f17] text-slate-200" data-testid="portal-layout">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-slate-800 bg-[#0d1320] lg:block">
        {SidebarContent}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <>
          <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setOpen(false)} />
          <aside className="fixed inset-y-0 left-0 z-50 w-64 border-r border-slate-800 bg-[#0d1320] lg:hidden">
            {SidebarContent}
          </aside>
        </>
      )}

      <div className="flex min-h-screen min-w-0 flex-1 flex-col lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-800 bg-[#0b0f17]/90 px-4 backdrop-blur lg:px-7">
          <div className="flex items-center gap-3">
            <button className="rounded-lg p-2 text-slate-300 hover:bg-slate-800 lg:hidden" onClick={() => setOpen(true)} data-testid="portal-menu-toggle">
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2"><span className="rs-ping absolute h-2 w-2 rounded-full bg-emerald-400" /><span className="relative h-2 w-2 rounded-full bg-emerald-400" /></span>
              <span className="hidden font-mono-data text-xs uppercase tracking-wider text-slate-400 sm:inline">Live Operations</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <a href={helpMailto(organization, user)} data-testid="portal-get-help-btn"
              className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2 text-sm font-semibold text-slate-300 transition-colors hover:border-blue-500/50 hover:text-white">
              <LifeBuoy className="h-4 w-4 text-blue-400" /> <span className="hidden sm:inline">Get help</span>
            </a>
            <Link to="/portal/notifications" data-testid="notifications-bell"
              className="relative rounded-lg border border-slate-800 bg-slate-900/60 p-2 text-slate-300 hover:border-slate-700 hover:text-white">
              <Bell className="h-5 w-5" />
              {unread > 0 && (
                <span data-testid="notifications-unread-count"
                  className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </Link>
          <div className="relative">
            <button onClick={() => setMenu((v) => !v)} data-testid="portal-user-menu"
              className="flex items-center gap-3 rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-1.5 hover:border-slate-700">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                {user?.name?.split(" ").map((p) => p[0]).slice(0, 2).join("")}
              </span>
              <span className="hidden text-left sm:block">
                <span className="block text-sm font-semibold text-white">{user?.name}</span>
                <span className="block text-[11px] font-semibold uppercase tracking-wide text-blue-400">{ROLE_LABEL[user?.role]}</span>
              </span>
              <ChevronDown className="h-4 w-4 text-slate-500" />
            </button>
            {menu && (
              <div className="absolute right-0 top-full mt-2 w-56 rounded-xl border border-slate-800 bg-[#111827] p-2 shadow-2xl" data-testid="user-dropdown">
                <div className="border-b border-slate-800 px-3 py-2">
                  <div className="text-sm font-semibold text-white">{user?.name}</div>
                  <div className="truncate text-xs text-slate-500">{user?.email}</div>
                </div>
                <button onClick={doLogout} data-testid="logout-btn"
                  className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-red-400 hover:bg-red-500/10">
                  <LogOut className="h-4 w-4" /> Sign out
                </button>
              </div>
            )}
          </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-7" onClick={() => menu && setMenu(false)}>
          <TrialBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
